import { Component, computed, input } from '@angular/core';

import { BadgeComponent, BadgeStatus } from '@shared/components/badge/badge.component';
import {
  formatCellValue,
  processStringValue
} from '@shared/components/table/table-cell/utils';

import { CellOverlayAction } from '../actions-overlay/actions-overlay.component';
import { CellWithOverlayActions } from '../table-cell.component';

/**
 * Cell value of a `string-with-badge` column. A column carries it from its `renderHook`,
 * which is the only place a row can decide whether its badge applies.
 */
export interface StringWithBadgeCellValue {
  text: string;
  badge?: string;
  badgeStatus?: BadgeStatus;
}

@Component({
  selector: 'app-string-with-badge-cell',
  standalone: true,
  imports: [BadgeComponent],
  template: `
    <span class="flex w-full min-w-0 items-center gap-1.5">
      <span class="line-clamp-3 min-w-0 break-after-all">{{ text() }}</span>
      @if (badge(); as label) {
        <app-badge class="shrink-0" [status]="badgeStatus()">{{ label }}</app-badge>
      }
    </span>
  `,
  styles: ``
})
export class StringWithBadgeCellComponent implements CellWithOverlayActions {
  availableCellActions: CellOverlayAction[] = ['copy'];
  // `copy_raw` JSON-stringifies the whole cell value, which here would copy the badge too.
  availableCellShiftActions: CellOverlayAction[] = [];
  valueAsString = (value: unknown) => formatCellValue(this.readText(value), 'string');

  value = input<unknown>(null);

  protected readonly text = computed(() => processStringValue(this.readText(this.value())));

  protected readonly badge = computed(() => this.parsed()?.badge ?? '');

  protected readonly badgeStatus = computed<BadgeStatus>(
    () => this.parsed()?.badgeStatus ?? 'info'
  );

  private readonly parsed = computed(() => this.asCellValue(this.value()));

  private asCellValue(value: unknown): StringWithBadgeCellValue | null {
    return typeof value === 'object' && value !== null
      ? (value as StringWithBadgeCellValue)
      : null;
  }

  /** A bare string still renders, so a column may put the badge behind a condition. */
  private readText(value: unknown): unknown {
    const cellValue = this.asCellValue(value);
    return cellValue === null ? value : cellValue.text;
  }
}
